import {
  ASSESSMENT_KINDS,
  type AssessmentEntry,
  CALL2_CALLS,
  CALL2_TRIES,
  type Progress,
  recordAssessment,
  skillsAt,
} from './assessment.ts';
import type { UserMemory } from './memory.ts';
import type { Passage } from './passage.ts';
import type { SpeechScores } from './scoring.ts';
import { addSample, emptySkills, overallCefr } from './skills.ts';
import type { TaskKind } from './tasks.ts';

/**
 * Check-ins (flow 002): the assessment set again, about every two weeks once a user practises, so the level moves on a
 * fresh measurement. Call 2's set and baseline stay the start; each check-in is a round with its own record of the
 * set, its own passage and, once finished, its result. Pure.
 */
export type CheckInRound = NonNullable<UserMemory['assessment']['rounds']>[number];

export const ROUND_EVERY_DAYS = 14;
// "Not today" waits this long before the check-in is offered again, so nobody hears it every call.
export const ROUND_DECLINE_WAIT_DAYS = 3;
const DAY_MS = 86_400_000;

const rounds = (memory: UserMemory) => memory.assessment.rounds ?? [];

/** The round under way: started and not finished. */
export function openRound(memory: UserMemory): CheckInRound | undefined {
  const last = rounds(memory).at(-1);
  return last && !last.finishedAt ? last : undefined;
}

/** The latest finished round, if any. */
export function lastRound(memory: UserMemory): CheckInRound | undefined {
  return rounds(memory).findLast((r) => r.finishedAt);
}

/** The latest check-in result: a finished round that scored nothing has none, and the one before it stands. */
export function latestResult(memory: UserMemory): CheckInRound['result'] {
  return rounds(memory).findLast((r) => r.result)?.result;
}

/** When they last did the set: the latest finished check-in, or reaching `assessed` (the end of call 2's set). */
export function lastSetAt(memory: UserMemory): string | undefined {
  if (memory.journey.stage !== 'assessed') return undefined;
  return lastRound(memory)?.finishedAt ?? memory.journey.at;
}

/**
 * Whether a call now would be a check-in: one is under way and not waiting after a "not today", or none is and the
 * last set is at least ROUND_EVERY_DAYS old.
 */
export function checkInDue(memory: UserMemory, now: Date): boolean {
  if (memory.journey.stage !== 'assessed') return false;
  const open = openRound(memory);
  if (open) return !open.declinedAt || now.getTime() - Date.parse(open.declinedAt) >= ROUND_DECLINE_WAIT_DAYS * DAY_MS;
  const last = lastSetAt(memory);
  return !!last && now.getTime() - Date.parse(last) >= ROUND_EVERY_DAYS * DAY_MS;
}

/** Starts the next round with its passage. A no-op while one is under way. */
export function startRound(memory: UserMemory, passage: Passage, at: string): UserMemory {
  if (openRound(memory)) return memory;
  const round: CheckInRound = { n: rounds(memory).length + 1, startedAt: at, items: {}, passage };
  return { ...memory, assessment: { ...memory.assessment, rounds: [...rounds(memory), round] } };
}

/** Kinds this round still needs, in the order they're asked for. */
export function roundLeft(round: CheckInRound | undefined): TaskKind[] {
  return round ? ASSESSMENT_KINDS.filter((k) => !round.items[k]) : [];
}

function withRound(memory: UserMemory, n: number, change: (r: CheckInRound) => CheckInRound): UserMemory {
  const list = rounds(memory).map((r) => (r.n === n ? change(r) : r));
  return { ...memory, assessment: { ...memory.assessment, rounds: list } };
}

/**
 * Records one kind in the round a call belongs to: the open one, or the one this call already finished (an answer that
 * landed after its call completed). The set's rule applies within the round: the first entry stands, except that an
 * answer replaces a skip and a scored answer an unscored one. A scored answer also goes into the round's own skills
 * (keyed, so a retry doesn't count twice), which is what its result and the level the coach hears come from.
 */
export function recordInRound(
  memory: UserMemory,
  kind: TaskKind,
  entry: AssessmentEntry,
  scored?: { scores: Extract<SpeechScores, { status: 'scored' }>; key: string },
): UserMemory {
  const round = openRound(memory) ?? rounds(memory).find((r) => r.finishedIn === entry.sessionId);
  if (!round) return memory;
  // The set's rule, on this round's items alone.
  const scratch = { ...memory, assessment: { ...memory.assessment, items: round.items } };
  const items = recordAssessment(scratch, kind, entry).assessment.items;
  const skills = scored
    ? addSample(round.skills ?? emptySkills(), scored.scores, entry.at, { kind, key: scored.key })
    : round.skills;
  return items === round.items && skills === round.skills
    ? memory
    : withRound(memory, round.n, (r) => ({ ...r, items, skills }));
}

/** What the coach hears during a check-in: the level its own answers show, and what the round still needs. */
export function roundProgress(memory: UserMemory): Progress {
  const round = openRound(memory);
  const skills = round?.skills ?? emptySkills();
  return { level: overallCefr(skills), levelFrom: skills.cefrHistory.length, kindsNotYetDone: roundLeft(round) };
}

type Call = { sessionId: string; at: string; openedTask: boolean };

/**
 * After a check-in call. One that did none of the round itself (this call opened no task and recorded nothing) is "not
 * today", even partway through a round, so the check-in waits a few days instead of returning next call. Otherwise it
 * counts towards the round's caps (call 2's: CALL2_TRIES calls that reached an exercise, CALL2_CALLS in all), after
 * which what's left is skipped as capped; and the round finishes once every kind is in.
 */
export function afterCheckInCall(memory: UserMemory, call: Call): UserMemory {
  const round = openRound(memory);
  if (!round) return memory;
  const didSome = call.openedTask || Object.values(round.items).some((e) => e.sessionId === call.sessionId);
  if (!didSome) {
    // A round an earlier call's answers completed (that call's completion hadn't run) just finishes; otherwise this
    // call did none of it: not today.
    return roundLeft(round).length
      ? withRound(memory, round.n, (r) => ({ ...r, declinedAt: call.at }))
      : finishRoundIfComplete(memory, call);
  }
  const tries = (round.tries ?? 0) + (call.openedTask ? 1 : 0);
  const calls = (round.calls ?? 0) + 1;
  let m = withRound(memory, round.n, (r) => ({ ...r, tries, calls, declinedAt: undefined }));
  if (tries >= CALL2_TRIES || calls >= CALL2_CALLS) {
    for (const kind of roundLeft(openRound(m))) {
      m = recordInRound(m, kind, {
        status: 'skipped',
        sessionId: call.sessionId,
        at: call.at,
        scored: false,
        capped: true,
      });
    }
  }
  return finishRoundIfComplete(m, call);
}

/** Finishes the open round once every kind is answered or skipped, saving the skills as they stand as its result. */
export function finishRoundIfComplete(memory: UserMemory, call: { sessionId: string; at: string }): UserMemory {
  const round = openRound(memory);
  if (!round || roundLeft(round).length) return memory;
  const result = resultOf(round, call.at);
  return withRound(memory, round.n, (r) => ({ ...r, finishedAt: call.at, finishedIn: call.sessionId, result }));
}

/**
 * An answer recorded after its check-in call completed. It may finish the round only now, or the call already finished
 * it with a result that didn't include this answer's scores.
 */
export function afterLateCheckInAnswer(memory: UserMemory, call: { sessionId: string; at: string }): UserMemory {
  if (openRound(memory)) return finishRoundIfComplete(memory, call);
  const finished = rounds(memory).find((r) => r.finishedIn === call.sessionId);
  if (!finished) return memory;
  const result = resultOf(finished, call.at);
  return result ? withRound(memory, finished.n, (r) => ({ ...r, result })) : memory;
}

// The round's own measurement: its scored answers alone. A round with nothing scored (an outage, or every kind skipped)
// has no result, and Progress keeps the one before.
function resultOf(round: CheckInRound, at: string) {
  return round.skills ? (skillsAt(round.skills, at) ?? undefined) : undefined;
}
